import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

const MAX_BYTES = 5 * 1024 * 1024;

interface Props {
    onBack: () => void;
    onSubmit: (file: File) => void;
}

export const StepImageInput: FC<Props> = ({ onBack, onSubmit }) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const tooLarge = !!file && file.size > MAX_BYTES;
    const empty = !!file && file.size === 0;
    const canSubmit = !!file && !tooLarge && !empty;

    return (
        <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
                단어가 포함된 이미지를 업로드하세요. 최대 5MB.
            </Typography>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setFile(f);
                }}
            />
            <Box>
                <Button variant="outlined" onClick={() => inputRef.current?.click()}>
                    {file ? '이미지 다시 선택' : '이미지 선택'}
                </Button>
            </Box>
            {file && (
                <Box display="flex" gap={2} alignItems="flex-start">
                    {previewUrl && (
                        <Box
                            sx={{
                                width: 200,
                                height: 200,
                                bgcolor: 'grey.100',
                                borderRadius: 1,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <img
                                src={previewUrl}
                                alt="preview"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            />
                        </Box>
                    )}
                    <Stack spacing={0.5}>
                        <Typography variant="body2">{file.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {(file.size / 1024).toFixed(1)} KB
                        </Typography>
                    </Stack>
                </Box>
            )}
            {tooLarge && <Alert severity="error">파일이 너무 큽니다 (5MB 이하).</Alert>}
            {empty && <Alert severity="error">빈 파일입니다.</Alert>}
            <Box display="flex" justifyContent="space-between" pt={1}>
                <Button onClick={onBack}>뒤로</Button>
                <Button variant="contained" disabled={!canSubmit} onClick={() => file && onSubmit(file)}>
                    다음
                </Button>
            </Box>
        </Stack>
    );
};
